/**
 * Overlay primitives on Base UI. One file because they share the same popup
 * surface treatment and the same enter/exit choreography — keeping them
 * together is what stops four slightly different shadows from appearing.
 */
import { Dialog as BaseDialog } from '@base-ui-components/react/dialog'
import { Menu as BaseMenu } from '@base-ui-components/react/menu'
import { Popover as BasePopover } from '@base-ui-components/react/popover'
import { Tooltip as BaseTooltip } from '@base-ui-components/react/tooltip'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

const surface =
  'z-50 rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface-raised)] ' +
  'shadow-lg outline-none origin-[var(--transform-origin)] ' +
  'transition-[transform,opacity] duration-150 ease-[var(--ease-out-soft)] ' +
  'data-[starting-style]:scale-[0.97] data-[starting-style]:opacity-0 ' +
  'data-[ending-style]:scale-[0.97] data-[ending-style]:opacity-0'

// --- Dialog -----------------------------------------------------------------

export const Dialog = BaseDialog.Root
export const DialogTrigger = BaseDialog.Trigger
export const DialogClose = BaseDialog.Close

export function DialogContent({
  className,
  children,
  size = 'md',
  ...props
}: React.ComponentProps<typeof BaseDialog.Popup> & { size?: 'sm' | 'md' | 'lg' | 'xl' }) {
  const widths = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' }
  return (
    <BaseDialog.Portal>
      <BaseDialog.Backdrop
        className={
          'fixed inset-0 z-40 bg-[oklch(0.15_0.01_265_/_0.42)] backdrop-blur-[2px] ' +
          'transition-opacity duration-150 data-[starting-style]:opacity-0 data-[ending-style]:opacity-0'
        }
      />
      <BaseDialog.Popup
        className={cn(
          surface,
          'fixed top-1/2 left-1/2 w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2',
          'max-h-[calc(100vh-4rem)] overflow-y-auto',
          widths[size],
          className
        )}
        {...props}
      >
        {children}
      </BaseDialog.Popup>
    </BaseDialog.Portal>
  )
}

export function DialogHeader({
  title,
  description,
  onClose,
}: {
  title: React.ReactNode
  description?: React.ReactNode
  onClose?: boolean
}) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-[var(--color-border)] px-5 py-4">
      <div className="min-w-0">
        <BaseDialog.Title className="text-base font-semibold text-[var(--color-fg)]">
          {title}
        </BaseDialog.Title>
        {description ? (
          <BaseDialog.Description className="mt-1 text-xs text-[var(--color-fg-muted)]">
            {description}
          </BaseDialog.Description>
        ) : null}
      </div>
      {onClose !== false ? (
        <BaseDialog.Close
          className="-mr-1 -mt-1 grid size-7 shrink-0 place-items-center rounded-[var(--radius-sm)] text-[var(--color-fg-subtle)] hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-fg)]"
          aria-label="Close"
        >
          <X className="size-4" />
        </BaseDialog.Close>
      ) : null}
    </div>
  )
}

export function DialogBody({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('px-5 py-4', className)} {...props} />
}

export function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'flex items-center justify-end gap-2 border-t border-[var(--color-border)] bg-[var(--color-surface-sunken)] px-5 py-3',
        className
      )}
      {...props}
    />
  )
}

// --- Menu -------------------------------------------------------------------

export const Menu = BaseMenu.Root
export const MenuTrigger = BaseMenu.Trigger

export function MenuContent({
  className,
  children,
  align = 'end',
  sideOffset = 6,
}: {
  className?: string
  children: React.ReactNode
  align?: 'start' | 'center' | 'end'
  sideOffset?: number
}) {
  return (
    <BaseMenu.Portal>
      <BaseMenu.Positioner align={align} sideOffset={sideOffset} className="z-50">
        <BaseMenu.Popup className={cn(surface, 'min-w-44 p-1', className)}>{children}</BaseMenu.Popup>
      </BaseMenu.Positioner>
    </BaseMenu.Portal>
  )
}

export function MenuItem({
  className,
  destructive,
  ...props
}: React.ComponentProps<typeof BaseMenu.Item> & { destructive?: boolean }) {
  return (
    <BaseMenu.Item
      className={cn(
        'flex cursor-default items-center gap-2 rounded-[var(--radius-sm)] px-2 py-1.5 text-sm outline-none',
        'data-[highlighted]:bg-[var(--color-surface-hover)]',
        destructive
          ? 'text-[var(--color-danger)] data-[highlighted]:bg-[var(--color-danger-bg)]'
          : 'text-[var(--color-fg)]',
        className
      )}
      {...props}
    />
  )
}

export function MenuLabel({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('px-2 pt-1.5 pb-1 text-2xs font-medium tracking-wide text-[var(--color-fg-subtle)] uppercase', className)}
      {...props}
    />
  )
}

export function MenuSeparator() {
  return <BaseMenu.Separator className="my-1 h-px bg-[var(--color-border)]" />
}

// --- Popover ----------------------------------------------------------------

export const Popover = BasePopover.Root
export const PopoverTrigger = BasePopover.Trigger
/**
 * Dismisses the popover from a control inside it. A click on the trigger or
 * outside closes it; a click on a button within it does not, so an action that
 * navigates somewhere else leaves the card floating over what it navigated to.
 */
export const PopoverClose = BasePopover.Close

export function PopoverContent({
  className,
  children,
  align = 'center',
  side = 'bottom',
  sideOffset = 8,
}: {
  className?: string
  children: React.ReactNode
  align?: 'start' | 'center' | 'end'
  side?: 'top' | 'bottom' | 'left' | 'right'
  sideOffset?: number
}) {
  return (
    <BasePopover.Portal>
      <BasePopover.Positioner align={align} side={side} sideOffset={sideOffset} className="z-50">
        <BasePopover.Popup className={cn(surface, className)}>{children}</BasePopover.Popup>
      </BasePopover.Positioner>
    </BasePopover.Portal>
  )
}

// --- Tooltip ----------------------------------------------------------------

/** Delay lives on the Provider in rc.0, so it is set once for the whole app. */
export function TooltipProvider({ children }: { children: React.ReactNode }) {
  return (
    <BaseTooltip.Provider delay={350} closeDelay={80}>
      {children}
    </BaseTooltip.Provider>
  )
}

/**
 * A tooltip with room to read in.
 *
 * Same hover behaviour and the same delay as `Tooltip` — it is the same
 * primitive — but on the light surface every other card uses, and wide enough
 * for a few lines of transcript. Deliberately NOT interactive: everything in it
 * is there to be read, and the actions live behind the click that opens the
 * panel. A card you have to travel into is a card that closes on the way.
 */
export function HoverCard({
  content,
  children,
  side = 'top',
}: {
  content: React.ReactNode
  children: React.ReactNode
  side?: 'top' | 'bottom' | 'left' | 'right'
}) {
  if (!content) return <>{children}</>
  return (
    <BaseTooltip.Root>
      <BaseTooltip.Trigger render={children as React.ReactElement<Record<string, unknown>>} />
      <BaseTooltip.Portal>
        <BaseTooltip.Positioner side={side} sideOffset={8} className="z-50">
          <BaseTooltip.Popup className={cn(surface, 'max-w-96 min-w-64 p-3')}>
            {content}
          </BaseTooltip.Popup>
        </BaseTooltip.Positioner>
      </BaseTooltip.Portal>
    </BaseTooltip.Root>
  )
}

export function Tooltip({
  content,
  children,
  side = 'top',
}: {
  content: React.ReactNode
  children: React.ReactNode
  side?: 'top' | 'bottom' | 'left' | 'right'
}) {
  if (!content) return <>{children}</>
  return (
    <BaseTooltip.Root>
      <BaseTooltip.Trigger render={children as React.ReactElement<Record<string, unknown>>} />
      <BaseTooltip.Portal>
        <BaseTooltip.Positioner side={side} sideOffset={6} className="z-50">
          <BaseTooltip.Popup
            className={
              'z-50 max-w-64 rounded-[var(--radius-sm)] bg-[var(--color-neutral-900)] px-2 py-1 ' +
              'text-2xs leading-relaxed text-[var(--color-neutral-50)] shadow-md ' +
              'transition-[transform,opacity] duration-100 ' +
              'data-[starting-style]:scale-95 data-[starting-style]:opacity-0 ' +
              'data-[ending-style]:scale-95 data-[ending-style]:opacity-0'
            }
          >
            {content}
          </BaseTooltip.Popup>
        </BaseTooltip.Positioner>
      </BaseTooltip.Portal>
    </BaseTooltip.Root>
  )
}
